import { VIEWABILITY_DURATION_MS, VIEWABILITY_THRESHOLD } from "./config";

// ViewabilityWatcher tracks N slots with a SINGLE IntersectionObserver
// instance shared across the page. Each slot fires its callback once it has
// been continuously ≥50% visible for VIEWABILITY_DURATION_MS — the IAB
// display viewability definition.
//
// Memory: O(observed slots). The Map entry is dropped on unobserve(); the
// watcher itself never holds strong refs beyond what the caller observes.

type Callback = () => void;

interface Entry {
  cb: Callback;
  timer: number;
}

export class ViewabilityWatcher {
  private readonly io: IntersectionObserver;
  private readonly entries = new Map<Element, Entry>();

  constructor() {
    this.io = new IntersectionObserver(
      (records) => {
        for (const r of records) {
          const item = this.entries.get(r.target);
          if (!item) continue;
          if (r.intersectionRatio >= VIEWABILITY_THRESHOLD) {
            if (item.timer === 0) {
              item.timer = window.setTimeout(
                () => this.fire(r.target),
                VIEWABILITY_DURATION_MS,
              );
            }
          } else if (item.timer !== 0) {
            clearTimeout(item.timer);
            item.timer = 0;
          }
        }
      },
      { threshold: [0, VIEWABILITY_THRESHOLD, 1] },
    );
  }

  observe(el: Element, cb: Callback): void {
    if (this.entries.has(el)) return;
    this.entries.set(el, { cb, timer: 0 });
    this.io.observe(el);
  }

  unobserve(el: Element): void {
    const item = this.entries.get(el);
    if (!item) return;
    if (item.timer !== 0) clearTimeout(item.timer);
    this.entries.delete(el);
    this.io.unobserve(el);
  }

  private fire(el: Element): void {
    const item = this.entries.get(el);
    if (!item) return;
    item.timer = 0;
    item.cb();
  }
}
